import React, { useEffect, useState } from 'react';
import { Popover } from 'antd';
import { Volume2 } from 'lucide-react';
import { SliderControl } from './toolbar/slider-control';
import { colors, dimensions, typography } from '../../theme';

const MAX_VOLUME = 0.5;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gain: number;
  onGainChange: (next: number) => void;
  children: React.ReactElement;
};

/**
 * Popover con slider de volumen para audio layers. Se ancla al children
 * y se abre desde el LayerContextMenu al clickear "Volumen…".
 *
 * El slider mueve con state local (`localGain`) para no llamar al dispatch
 * del store en cada tick del drag. Solo activa `onGainChange` una vez al soltar
 * (`onChangeComplete` de antd Slider). Sin esto, el drag disparaba ~60 dispatches/s
 * → cadena de re-renders y update del audio-sync effect por cada uno.
 */
export const VolumePopover: React.FC<Props> = ({ open, onOpenChange, gain, onGainChange, children }) => {
  const [localGain, setLocalGain] = useState(gain);

  // Sync local con el prop cuando cambia desde afuera (ej. reload del proyecto,
  // otro popover editó el mismo layer, undo/redo).
  useEffect(() => {
    setLocalGain(gain);
  }, [gain]);

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      trigger='click'
      placement='top'
      content={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: dimensions.spacing.md,
            padding: dimensions.spacing.xs
          }}
          onClick={e => e.stopPropagation()}
        >
          <SliderControl
            value={localGain}
            onChange={setLocalGain}
            onChangeComplete={onGainChange}
            max={MAX_VOLUME}
            iconLeft={<Volume2 size={dimensions.size.iconSm} />}
            iconRight={
              <span
                style={{
                  fontSize: typography.fontSize.sm,
                  color: colors.textSecondary,
                  fontFamily: typography.fontFamily.ui,
                  minWidth: 32,
                  textAlign: 'right'
                }}
              >
                {Math.round((localGain / MAX_VOLUME) * 100)}%
              </span>
            }
            width={160}
            title='Volumen'
          />
        </div>
      }
    >
      {children}
    </Popover>
  );
};
